import { ApiClient } from '_core/api/client';
import { ListResponse } from '_core/api/types';
import { serializable } from '_core/serializable';

const BASE_ENDPOINT = '/wagon_coo_notes';

interface IWagonCooNotesSerialized {
  created: string;
  createdBy: number | null;
  dislocation: number;
  files: string[];
  id: number;
  note: string;
  operation: string;
  wagon: string;
}

interface IWagonCooNote extends Omit<IWagonCooNotesSerialized, 'created'> {
  created: Date;
}

@serializable
export class WagonNote implements IWagonCooNote {
  @serializable.datetime readonly created;
  readonly createdBy;
  readonly dislocation;
  readonly files;
  readonly id;
  readonly note;
  readonly operation;
  readonly wagon;

  static fromJSON(serialized: IWagonCooNotesSerialized) {
    return serializable.fromJSON(WagonNote, serialized);
  }

  constructor(wagonNote: IWagonCooNote) {
    this.created = wagonNote.created;
    this.createdBy = wagonNote.createdBy;
    this.dislocation = wagonNote.dislocation;
    this.files = wagonNote.files;
    this.id = wagonNote.id;
    this.note = wagonNote.note;
    this.operation = wagonNote.operation;
    this.wagon = wagonNote.wagon;
  }
}

export async function fetchWagonCooNotes(
  api: ApiClient,
  {
    page,
    pageSize,
    wagon,
  }: {
    page?: number;
    pageSize?: number;
    wagon?: string;
  } = {}
): Promise<ListResponse<WagonNote>> {
  const response = await api.get<ListResponse<IWagonCooNotesSerialized>>(
    BASE_ENDPOINT,
    {
      page,
      pageSize,
      wagon,
    }
  );

  return {
    ...response,
    results: response.results.map(WagonNote.fromJSON),
  };
}

export async function createWagonCooNote(
  api: ApiClient,
  {
    created,
    dislocation,
    files,
    note,
    operation,
    wagon,
  }: {
    created: Date;
    dislocation: number;
    files: string[];
    note: string;
    operation: string;
    wagon: string;
  }
) {
  await api.post(BASE_ENDPOINT, {
    created,
    dislocation,
    files,
    note,
    operation,
    wagon,
  });
}

export async function updateWagonCooNote(
  api: ApiClient,
  id: number,
  note: WagonNote
) {
  const response = await api.put<IWagonCooNotesSerialized>(
    `${BASE_ENDPOINT}/${id}`,
    note
  );

  return WagonNote.fromJSON(response);
}

export async function deleteWagonCooNote(api: ApiClient, id: number) {
  await api.delete(`${BASE_ENDPOINT}/${id}`);
}

export function getDownloadWagonCooNotesXlsxUrl(api: ApiClient, wagon: string) {
  return api.buildApiUrl(`${BASE_ENDPOINT}/download_xlsx`, { wagon });
}
